import { useState } from 'react';
import { Layers } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import Button from '../components/Button';
import StoryCardItem from '../components/StoryCardItem';
import Tag from '../components/Tag';
import { sortSavedCardsByEngagement, sortFilteredCards } from '../lib/sortingUtils';
import { IssueTag } from '../lib/types';
import Header from '../components/Header';
import { trackEvent } from '../lib/analytics';

const TOPIC_FILTERS = [
  'All',
  'Conflict',
  'Education',
  'Environment',
  'Health',
  'Housing',
  'Identity',
  'Justice',
  'Media',
  'Migration',
  'Work',
];

export default function Saved() {
  const {
    savedCardIds,
    navigateTo,
    songEngagements,
    getCards,
  } = useApp();

  const { user, isLoggedOut } = useEffectiveUser();
  const isGuest = isLoggedOut || !user || !user.id;

  const [topicFilter, setTopicFilterState] = useState('All');

  const setTopicFilter = (topic: string) => {
    setTopicFilterState(topic);

    // Track topic filter selection for analytics (skip 'All' filter)
    if (topic !== 'All') {
      trackEvent({
        event_name: 'topic_filter_selected',
        topic_tag: topic,
      });
    }
  };

  const allCards = getCards();
  console.log('[Collection] All cards from getCards():', allCards.length);
  console.log('[Collection] All card IDs from getCards():', allCards.map(c => c.id));
  console.log('[Collection] savedCardIds:', savedCardIds);
  console.log('[Collection] Filtering allCards by savedCardIds...');
  const allSavedCards = allCards.filter(card => savedCardIds.includes(card.id));
  console.log('[Collection] Filtered saved cards:', allSavedCards.length, 'cards');
  console.log('[Collection] Saved card IDs:', allSavedCards.map(c => c.id));
  const savedCardsSortedByEngagement = sortSavedCardsByEngagement(allSavedCards, songEngagements);

  const filteredCards = topicFilter === 'All'
    ? savedCardsSortedByEngagement
    : sortFilteredCards(savedCardsSortedByEngagement, topicFilter as IssueTag);

  console.log('[Collection] After topic filter (topicFilter=' + topicFilter + '):', filteredCards.length, 'cards');
  console.log('[Collection] Final filtered card IDs:', filteredCards.map(c => c.id));

  const handleCardClick = (cardId: string) => {
    navigateTo('card-detail', cardId);
  };

  // Guest state
  if (isGuest) {
    return (
      <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
        <Header mode="title" title="Collection" />

        <div className="flex flex-col items-center justify-center px-8 pt-24">
          <div className="w-20 h-20 bg-[#40E0D0]/10 rounded-full flex items-center justify-center mb-6">
            <Layers className="w-10 h-10 text-[#40E0D0]" />
          </div>

          <h2 className="text-[22px] font-semibold text-[#F5F5F5] mb-3 text-center" style={{ letterSpacing: '-0.005em' }}>
            Your Collection
          </h2>

          <p className="text-[15px] text-[#A0A0A0] text-center mb-8 max-w-sm" style={{ lineHeight: '1.55' }}>
            Save stories to build your civic collection.
          </p>

          <div className="flex flex-col gap-3 w-full max-w-sm">
            <Button
              variant="primary"
              onClick={() => navigateTo('auth', undefined, { authMode: 'signup' })}
            >
              Create Account
            </Button>

            <Button
              variant="secondary"
              onClick={() => navigateTo('auth', undefined, { authMode: 'signin' })}
            >
              Sign In
            </Button>
          </div>

          <button
            onClick={() => navigateTo('explore')}
            className="text-[13px] text-[#808080] mt-5 hover:text-[#A0A0A0] transition-colors"
          >
            Browse Stories Instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <Header mode="title" title="Collection" />

      {/* Filter chips - only show when there are saved cards */}
      {allSavedCards.length > 0 && (
        <div className="max-w-[1000px] mx-auto w-full px-5 md:px-8 pt-5 pb-4">
          {/* Mobile: Scrollable horizontal */}
          <div className="md:hidden">
            <div className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-emerald-900/50 scrollbar-track-transparent -mx-1">
              <div className="flex gap-2 px-1" style={{ width: 'max-content' }}>
                {TOPIC_FILTERS.map(topic => (
                  <Tag
                    key={topic}
                    variant="chip"
                    selected={topicFilter === topic}
                    onClick={() => setTopicFilter(topic)}
                    compact
                  >
                    {topic}
                  </Tag>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop: Scrollable horizontal */}
          <div className="hidden md:block">
            <div className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-emerald-900/50 scrollbar-track-transparent -mx-1">
              <div className="flex gap-2 px-1" style={{ width: 'max-content' }}>
                {TOPIC_FILTERS.map(topic => (
                  <Tag
                    key={topic}
                    variant="chip"
                    selected={topicFilter === topic}
                    onClick={() => setTopicFilter(topic)}
                  >
                    {topic}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

          {/* Song cards list */}
          {filteredCards.length > 0 ? (
            <div className="px-5 md:px-8 pb-4 max-w-[1000px] mx-auto w-full space-y-4">
              {filteredCards.map((card) => (
                <StoryCardItem
                  key={card.id}
                  card={card}
                  onClick={() => handleCardClick(card.id)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 px-8 max-w-[1000px] mx-auto">
              <Layers className="w-12 h-12 text-[#A0A0A0] mb-4" />
              <p className="text-[14px] text-[#A0A0A0] text-center">
                {allSavedCards.length === 0
                  ? 'Add cards to your collection'
                  : 'No cards match this filter'}
              </p>
              {allSavedCards.length > 0 && topicFilter !== 'All' && (
                <button
                  onClick={() => setTopicFilter('All')}
                  className="text-[14px] text-[#40E0D0] underline mt-2"
                >
                  Clear Filter
                </button>
              )}
            </div>
          )}
    </div>
  );
}
